import {getFullMonthText} from '@wix/events-moment-commons'
import {useStyles} from '@wix/tpa-settings/react'
import {CALENDAR_LAYOUT} from '@wix/wix-events-commons-statics'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {CSSProperties} from 'react'
import stylesParams from '../../../../stylesParams'
import {useWidgetState} from '../../../hooks/state-provider'
import {
  getDayMonthText,
  getDaysInGrid,
  getFullReferenceMonthName,
  getSelectedDate,
  getWeekdaysFromStartWeekday,
} from '../../../selectors/calendar-layout'
import {
  POPUP_LAYOUT_BREAKPOINT,
  SIDE_BY_SIDE_POPUP_FULL_HEIGHT_BREAKPOINT,
  VERTICAL_POPUP_FULL_HEIGHT_BREAKPOINT,
} from '../../../utils/calendar'
import {CalendarCell} from './cell'
import s from './grid.scss'
import {CalendarPopup} from './popup'

export const CalendarGrid = () => {
  const {isMobile} = useEnvironment()
  const {get} = useStyles()
  const {t} = useTranslation()

  const calendarLayoutType = get(stylesParams.calendarLayoutType)
  const monthly = calendarLayoutType === CALENDAR_LAYOUT.MONTHLY
  const shownDays = useWidgetState(state => getDaysInGrid(state, monthly))
  const weekdayNames = useWidgetState(getWeekdaysFromStartWeekday)
  const selectedDate = useWidgetState(getSelectedDate)
  const monthName = useWidgetState(state => getFullReferenceMonthName(state, getFullMonthText))
  const weeklyStartDateText = useWidgetState(state => getDayMonthText(state, shownDays[0]))
  const weeklyEndDateText = useWidgetState(state => getDayMonthText(state, shownDays[shownDays.length - 1]))
  const calendarHeight = get(stylesParams.calendarHeight)
  const sideBySidePopup = calendarHeight <= POPUP_LAYOUT_BREAKPOINT
  const popupFullHeight = sideBySidePopup
    ? calendarHeight <= SIDE_BY_SIDE_POPUP_FULL_HEIGHT_BREAKPOINT
    : calendarHeight <= VERTICAL_POPUP_FULL_HEIGHT_BREAKPOINT

  const popupClassName = classNames(
    s.popupPosition,
    isMobile ? s.mobilePopup : sideBySidePopup ? s.sideBySidePopup : s.popup,
    {
      [s.weeklyPopupHeight]: !isMobile && !monthly,
      [s.popupFullHeight]: !isMobile && monthly && popupFullHeight,
      [s.popupHeight]: !isMobile && monthly && !popupFullHeight,
    },
  )

  const calendarLabel = monthly
    ? t('a11y.monthlyCalendarLabel', {monthName})
    : t('a11y.weeklyCalendarLabel', {
        weeklyStartDate: weeklyStartDateText,
        weeklyEndDate: weeklyEndDateText,
      })

  const renderWeek = (week = 0) => (
    <div
      key={week}
      role="row"
      className={classNames(s.row, {[s.bottomRow]: week >= 3})}
      style={{'--row': week} as CSSProperties}
    >
      {new Array(7).fill(null).map((__, weekDay) => {
        const timestamp = shownDays[week * 7 + weekDay]

        return (
          <div
            key={timestamp}
            role="gridcell"
            className={classNames(s.day, {
              [s.leftColumn]: weekDay < 4,
              [s.rightColumn]: weekDay > 4,
              [s.lastColumn]: weekDay === 6,
            })}
            style={{'--column': weekDay} as CSSProperties}
          >
            <CalendarCell timestamp={timestamp} columnIndex={weekDay} />
            {!isMobile && selectedDate === timestamp && <CalendarPopup className={popupClassName} />}
          </div>
        )
      })}
    </div>
  )

  return (
    <div className={s.container} role="grid" aria-label={calendarLabel}>
      <div aria-hidden className={s.weekDays}>
        {weekdayNames.map(name => (
          <div key={name} className={s.weekDay}>
            {name}
          </div>
        ))}
      </div>
      <div className={classNames(s.grid, {[s.weekly]: !monthly})}>
        {monthly ? new Array(6).fill(null).map((_, week) => renderWeek(week)) : renderWeek()}
        {!!selectedDate && isMobile && <CalendarPopup className={popupClassName} />}
      </div>
    </div>
  )
}
